import { TranslatableError } from '@/i18n/errors';
import { apiFetch } from '@/lib/api';

/**
 * Web Push enrolment.
 *
 * On iOS this only works for a PWA the user installed to their home screen,
 * which is why email is a first-class reminder channel rather than a fallback —
 * for a large share of users push can never be granted at all.
 */

export type PushState = 'unsupported' | 'default' | 'granted' | 'denied';

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushState(): PushState {
  if (!pushSupported()) {
    return 'unsupported';
  }

  return Notification.permission as PushState;
}

/**
 * The VAPID key arrives base64url-encoded and `applicationServerKey` wants raw
 * bytes; there is no browser API that does this conversion.
 */
function decodeKey(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.padEnd(
    base64Url.length + ((4 - (base64Url.length % 4)) % 4),
    '=',
  );
  const binary = atob(padded.replaceAll('-', '+').replaceAll('_', '/'));

  // Backed by an explicit ArrayBuffer: `Uint8Array.from` produces
  // `ArrayBufferLike`, which could be a SharedArrayBuffer, and
  // `applicationServerKey` will not accept that.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();

  if (existing) {
    return existing;
  }

  // `ready` resolves only once a worker is active, so an install still in
  // flight is waited out rather than treated as absence.
  return navigator.serviceWorker.ready;
}

export async function currentEndpoint(): Promise<string | null> {
  if (!pushSupported()) {
    return null;
  }

  const subscription = await (await registration()).pushManager.getSubscription();

  return subscription?.endpoint ?? null;
}

export async function enablePush(): Promise<void> {
  if (!pushSupported()) {
    throw new TranslatableError('errors.push_unsupported');
  }

  const permission = await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new TranslatableError('errors.push_blocked');
  }

  const { public_key: publicKey } = await apiFetch<{ public_key: string | null }>(
    '/push/key',
  );

  if (!publicKey) {
    throw new TranslatableError('errors.push_not_configured');
  }

  const active = await registration();

  // Reuses whatever is already there, so a second enable does not churn the
  // endpoint and orphan the row the server holds.
  const subscription =
    (await active.pushManager.getSubscription()) ??
    (await active.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    }));

  const payload = subscription.toJSON();

  await apiFetch('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: payload.keys,
    }),
  });
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) {
    return;
  }

  const subscription = await (await registration()).pushManager.getSubscription();

  if (!subscription) {
    return;
  }

  // Told to the server first: if the browser-side unsubscribe succeeds and the
  // request then fails, the server keeps sending to an endpoint nobody owns.
  await apiFetch('/push/subscribe', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });

  await subscription.unsubscribe();
}
